import { useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { clean, fmtN, money, num, titleCase, toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { feeSchedule, fmtCents } from '../utils/fees';

const field = 'flex flex-col gap-2';
const fieldKey = 'text-[13px] font-medium leading-[18px]';
const box = 'flex h-14 items-center gap-2 rounded-md bg-[--q-paper]';
const currency = 'text-[20px] leading-6 text-[--q-muted]';
const input =
  'h-full w-0 min-w-0 flex-auto bg-transparent p-0 font-heading text-[28px] leading-8 text-[--q-ink] [border:0] [outline:none] placeholder:text-[--q-muted] placeholder:opacity-100 focus-visible:[outline:none]';
const stepper =
  'inline-flex h-11 w-11 items-center justify-center rounded-md bg-[--q-surface] text-[22px] leading-[22px] text-[--q-ink] disabled:cursor-default disabled:opacity-40';
const hint = 'text-[13px] leading-[18px] text-[--q-muted] [&_p]:m-0';
const row =
  'flex min-h-[52px] items-center justify-between gap-4 px-5 text-base leading-[22px] [&+&]:border-t [&+&]:border-t-[--q-border] max-[767px]:min-h-0 max-[767px]:p-0 max-[767px]:text-[15px] max-[767px]:leading-[18px] max-[767px]:[&+&]:[border-top:0]';
const note = 'ml-2.5 text-[12px] font-medium uppercase leading-4 tracking-[.06em] text-[--q-muted] max-[767px]:hidden';
const amount = 'text-right font-heading text-[20px] font-normal leading-[26px] max-[767px]:text-[18px] max-[767px]:leading-[22px]';

const twoDp = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "Build your monthly estimate": rent typed in, pets counted, other monthly costs, then the sum. Never a quote. */
export function FeeEstimate() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const s = feeSchedule(p);
  const plans = ((p.floorplans as Array<{ floorplan_name?: string; floorplan_price_min?: string }>) || [])
    .map((f) => ({ name: titleCase(clean(f.floorplan_name)), price: num(clean(f.floorplan_price_min)) }))
    .filter((f): f is { name: string; price: number } => !!f.price)
    .sort((a, b) => a.price - b.price);
  const sample = plans[0] || null;
  const petRent = p.property_ispetfriendly ? num(clean(p.property_monthly_pet_rent)) : null;
  const maxPets = num(p.property_max_pets) || 2;

  const [rentText, setRentText] = useState(sample ? fmtN(sample.price) : '');
  const [pets, setPets] = useState(petRent ? 1 : 0);
  const [otherText, setOtherText] = useState('');
  // The sample plan can arrive after first render; fill the field only while it is still empty.
  useEffect(() => {
    if (sample) setRentText((t) => t || fmtN(sample.price));
  }, [sample?.price]);

  if (s.monthlyLo == null && !editing) return null;

  const rent = num(rentText) || 0;
  const other = num(otherText) || 0;
  const essentials = s.monthlyLo || 0;
  const petTotal = petRent ? pets * petRent : 0;
  const total = rent + essentials + petTotal + other;
  const parts = [rent, essentials, petTotal, other].filter((x, i) => i < 2 || x > 0).map(twoDp);
  const ranged = s.monthlyHi != null && s.monthlyLo != null && s.monthlyHi > s.monthlyLo + 0.001;

  const rentHint = `Type the rent for the home you want.${sample ? ` Sample is the ${sample.name} minimum.` : ''}`;
  const petHint = petRent ? `${money(petRent)} each month.${maxPets > 1 ? ` Two pets are ${money(petRent * 2)}.` : ''} The one-time pet fee is not included.` : '';

  return (
    <section
      className="flex flex-col gap-9 bg-[--q-surface] px-[--gutter] pb-16 pt-14 max-[767px]:gap-4 max-[767px]:pb-9 max-[767px]:pt-8"
      id="estimate"
    >
      <div className="flex max-w-[700px] flex-col gap-3" data-reveal>
        <RichTextContent path="feeestimate.eyebrow" as="div" className="eyebrow" />
        <RichTextContent
          path="feeestimate.title"
          as="h2"
          className="m-0 font-heading text-[36px] font-light leading-10 tracking-[--q-tracking-tight] [&_p]:m-0 max-[767px]:text-[32px] max-[767px]:leading-9"
        />
      </div>
      <div className="flex flex-col gap-7 rounded-lg bg-[--q-paper] p-8 max-[767px]:gap-4 max-[767px]:bg-transparent max-[767px]:p-0">
        <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-6 max-[991px]:grid-cols-[minmax(0,1fr)] max-[767px]:gap-4">
          <label className={field}>
            <span className={fieldKey}>Base rent</span>
            <span className={`${box} border-2 border-[--q-primary] px-[15px]`}>
              <span className={currency}>$</span>
              <input className={input} inputMode="decimal" value={rentText} onChange={(e) => setRentText(e.target.value.replace(/[^\d.,]/g, ''))} />
            </span>
            <RichTextContent path="feeestimate.rent_hint" as="span" className={hint} defaultValue={toParagraph(rentHint)} />
          </label>
          {petRent ? (
            <div className={field}>
              <span className={fieldKey} id="est-pets">Pet rent</span>
              <span
                className={`${box} justify-between border border-[--q-border] px-2 focus-within:border-2 focus-within:border-[--q-primary] focus-within:px-[7px]`}
                role="group"
                aria-labelledby="est-pets"
              >
                <button className={stepper} type="button" aria-label="One fewer pet" disabled={pets <= 0} onClick={() => setPets(Math.max(0, pets - 1))}>−</button>
                <output className="font-heading text-[28px] leading-8" aria-live="polite">{pets}</output>
                <button className={stepper} type="button" aria-label="One more pet" disabled={pets >= maxPets} onClick={() => setPets(Math.min(maxPets, pets + 1))}>+</button>
              </span>
              <span className={hint}>{petHint}</span>
            </div>
          ) : null}
          <label className={field}>
            <span className={fieldKey}>Other monthly</span>
            <span className={`${box} border border-[--q-border] px-4 focus-within:border-2 focus-within:border-[--q-primary] focus-within:px-[15px]`}>
              <span className={currency}>$</span>
              <input className={input} inputMode="decimal" placeholder="0" value={otherText} onChange={(e) => setOtherText(e.target.value.replace(/[^\d.,]/g, ''))} />
            </span>
            <RichTextContent path="feeestimate.other_hint" as="span" className={hint} />
          </label>
        </div>

        <div className="flex flex-col rounded-lg bg-[--q-surface] px-2 pb-7 pt-2 max-[767px]:gap-3 max-[767px]:bg-[--q-paper] max-[767px]:p-4">
          <div className={row}>
            <span>Base rent</span>
            <b className={amount}>{twoDp(rent)}</b>
          </div>
          <div className={row}>
            <span>
              Monthly essentials <small className={note}>Required{ranged ? ' · lowest, varies by home' : ''}</small>
            </span>
            <b className={amount}>{fmtCents(essentials)}</b>
          </div>
          {petRent ? (
            <div className={row}>
              <span>Pet rent · {pets} {pets === 1 ? 'pet' : 'pets'}</span>
              <b className={amount}>{twoDp(petTotal)}</b>
            </div>
          ) : null}
          {other > 0 && (
            <div className={row}>
              <span>Other monthly</span>
              <b className={amount}>{twoDp(other)}</b>
            </div>
          )}
          <div className="flex items-end justify-between gap-6 border-t border-t-[--q-border] px-5 pb-0 pt-5 max-[767px]:grid max-[767px]:gap-1.5 max-[767px]:px-0 max-[767px]:pt-3 max-[767px]:[grid-template-areas:'label'_'value'_'note']">
            <div className="flex max-w-[640px] flex-col gap-1.5 max-[767px]:contents">
              <RichTextContent path="feeestimate.total_label" as="div" className="eyebrow [&_p]:inline max-[767px]:[grid-area:label]" />
              <p className="m-0 text-[13px] leading-[18px] text-[--q-muted] [&_p]:m-0 [&_p]:inline max-[767px]:[grid-area:note]">
                {parts.join(' + ')}. <RichTextContent path="feeestimate.total_note" as="span" />
              </p>
            </div>
            <div
              className="whitespace-nowrap font-heading text-[48px] leading-[52px] tracking-[--q-tracking-tight] max-[767px]:text-[40px] max-[767px]:leading-[44px] max-[767px]:[grid-area:value]"
              aria-live="polite"
            >
              {twoDp(total)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeEstimate',
  displayName: 'Fee Estimate',
  tokens: {
    'feeestimate.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Not a quote</p>' },
    'feeestimate.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Build your monthly estimate</p>' },
    'feeestimate.rent_hint': {
      label: 'Base Rent Hint',
      description: 'Until edited, names the plan whose lowest rent fills the field.',
      type: 'richtext',
      defaultValue: '<p>Type the rent for the home you want.</p>',
    },
    'feeestimate.other_hint': { label: 'Other Monthly Hint', type: 'richtext', defaultValue: '<p>Optional. Add renters insurance only if you already know the premium.</p>' },
    'feeestimate.total_label': { label: 'Total Label', type: 'richtext', defaultValue: '<p>Sample monthly cost</p>' },
    'feeestimate.total_note': { label: 'Total Note', type: 'richtext', defaultValue: '<p>Includes the rent entered above. An estimate, not a quote.</p>' },
  },
};

(FeeEstimate as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeEstimate;
